//Nov4 AssetCard.jsx:
// ────────────────────────────────────────────────────────────────
// src/components/AssetManager/AssetCard.jsx
// (May-5 – overlay: name + metadata stacked in bottom-left)
//  ✱ Hover-reveal: ⋮  + overlay appear only on hover
// ────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardMedia,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Skeleton, // ← Skeleton added
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { ref, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
import './styles/assetCard.css'; // hover rules live here
import { getThumbAspect } from '../utils/assetLayout';
import {
  fetchAssetById,
  deleteAssetViaCloudFunction,
} from '../utils/frontend_assetManager';
import { useAuth } from '../contexts/AuthContext';
import ConfirmDeleteDialog from './ConfirmDeleteDialog';
import VersionsDrawer from '../AssetManager/VersionsDrawer';
import LayersIcon from '@mui/icons-material/Layers';


// 1×1 transparent SVG used as a safe fallback (never 404s).
const FALLBACK =
  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" ' +
  'width="1" height="1"/>';

// Simple per-session cache for signed preview URLs returned by Cloud Functions.
// This replaces client-side Storage getDownloadURL() for private DAM assets.
const urlCache =
  typeof window !== 'undefined' ? (window.__assetUrlCache ||= new Map()) : new Map();

/**
 * Get a short-lived server-signed preview URL for an asset.
 * - Uses Cloud Function: getAssetById (fetchAssetById client helper)
 * - Caches per session to avoid repeated signing calls
 *
 * @param {string} assetId
 * @param {string} token
 * @param {string|null} prefer  // null | 'micro' | 'original'
 * @returns {Promise<string|null>}
 */
const getSigned = async (assetId, token, prefer = null) => {
  if (!assetId || !token) return null;

  const cacheKey = `${assetId}@${prefer || 'default'}`;
  if (urlCache.has(cacheKey)) return urlCache.get(cacheKey);

  const data = await fetchAssetById(
    assetId,
    token,
    { includeUrls: true, includeMeta: false, prefer }
  );

  const url =
    (data && typeof data.previewUrl === 'string' && data.previewUrl.trim())
      ? data.previewUrl.trim()
      : null;

  if (url) urlCache.set(cacheKey, url);
  return url;
};

// Preload an image URL so we can swap src/srcSet without re-showing the skeleton
const preload = (url) =>
  new Promise((resolve, reject) => {
    if (!url) return reject(new Error('No URL to preload'));
    const img = new Image();
    img.onload = () => resolve(url);
    img.onerror = reject;
    img.decoding = 'async';
    img.loading = 'eager';
    img.src = url;
  });

// only treat real browser URLs as preloadable image sources.
// Prevents attempting to preload Storage paths like "assets/org/assetId/v1_grid_400.webp".
const isBrowserUrl = (s) =>
  typeof s === 'string' &&
  (s.startsWith('http://') || s.startsWith('https://') || s.startsWith('blob:') || s.startsWith('data:'));

/* ╭─────────────────────────────────────────────────────────────╮
   │  AssetCard                                                  │
   ╰─────────────────────────────────────────────────────────────╯ */
function AssetCard({
  asset,
  onClick = () => {},
  onDetails,          // ← no default
  onDeleted,          // ← no default
  selected = false,
  sx: sxProp = {},
  overrideAspect = null, // force aspect-ratio when provided
  forceContain = false,  // avoid crop when clamped
  renderWidth = null,    // px width of the tile (improves <img sizes>)
  loadingPriority = 'auto', // ← NEW: 'high' | 'auto'
}) {
  // Auth (for CF signed URLs)
  const { currentUser, getToken } = useAuth();

  /* Prefer server-provided signed URL for the thumbnail (fast, private) */
  const preUrl = asset?.thumbUrl || null;

  /* Local optimistic preview (File/Blob) set by AssetMetadataContext */
  const localSrc = asset?.localSrc || null;

  /* Cloud upgrade targets (server gives either cloudSrc or grid u400/u800) */
  const cloud400 = asset?.cloudSrc || asset?.grid?.u400 || null;
  const cloud800 = asset?.grid?.u800 || null;

  /* 1. Best Storage *path* (fallback when no URLs above) */
  /* Prefer the sharpest sensible rendition per type.
     - icon      → icon2x (crisp)
     - otherwise → thumbnail (now 400w cover)
     - legacy    → if old small thumb (<300w), prefer medium
     - fallback  → original → literal URL → null */
  const v = asset?.versions || {};
  const thumbTooSmall =
    typeof v?.thumbnail?.width === 'number' && v.thumbnail.width < 300;


  const pickBestPath = () => {
    if (asset?.assetType === 'icon' && v.icon2x?.path) return v.icon2x.path;
    if (v.thumbnail?.path && !thumbTooSmall) return v.thumbnail.path;
    if (v.medium?.path) return v.medium.path;
    if (v.original?.path) return v.original.path;
    return asset?.thumbnail || null; // (rare) literal URL/placeholder
  };

  const imgPath = pickBestPath();

  const [imgSrc, setImgSrc] = useState(localSrc || preUrl || FALLBACK);

  const [imgLoaded, setImgLoaded] = useState(false);
  const [srcSet, setSrcSet] = useState(undefined);

  // DEBUG: measure time from setting imgSrc → first onLoad
  const paintStartRef = useRef(0);

  // Versions drawer state + visibility flag
  const [versionsOpen, setVersionsOpen] = useState(false);

  // DEBUG: verify first-paint source (is thumbUrl already grid 400?)
  // Enable in DevTools:
  //   window.__DEBUG_CARD_FIRST_PAINT__ = true
  useEffect(() => {
    try {
      if (typeof window === 'undefined') return;
      if (window.__DEBUG_CARD_FIRST_PAINT__ !== true) return;

      if (asset?.grid?.u400) {
        console.log('[Card:first-paint]', {
          id: asset.id || asset.assetId,
          thumbUrlIsGrid: preUrl === asset.grid.u400,
        });
      }
    } catch (_) {
      // non-fatal
    }
 }, [preUrl, asset?.grid?.u400]);

  // Show the icon only when there are multiple versions
  const hasVersions =
    (typeof asset?.versionNumber === 'number' && asset.versionNumber > 1) ||
    Boolean(asset?.previousAssetId);

/* 3️⃣  Load strategy (enterprise, silent upgrade):
      1) Paint immediately with backend pre-signed thumbUrl (fast, private).
      2) Opportunistically upgrade to UNCROPPED grid thumbs (400 / 800) if present.
         - Preload before swapping so we never re-show the skeleton.
         - Only fetch 2x when device DPR > 1.2 and 800 exists.
      3) If neither is available, fall back to the best legacy Storage path.
*/
useEffect(() => {
  let cancelled = false;

  (async () => {
    /* 0) Local optimistic preview takes precedence.
       - If localSrc exists, show it immediately.
       - If/when cloud400 appears, preload and swap silently (keep skeleton off).
       - We DO NOT revoke here; Context handles revoke timing after swap. */
    if (localSrc) {
      if (!cancelled && imgSrc !== localSrc) {
        if (!imgLoaded) {
          setImgLoaded(false);
          paintStartRef.current = performance.now(); // DEBUG start
        }
        setImgSrc(localSrc);
        setSrcSet(undefined);
      }

      // Upgrade to cloud (either cloudSrc or grid u400) when present
      if (isBrowserUrl(cloud400)) {
        try {
          const ready400 = await preload(cloud400);
          if (!cancelled) {
            // Keep bitmap visible during upgrade
            if (imgSrc !== ready400) {
              paintStartRef.current = performance.now(); // DEBUG start
              setImgSrc(ready400);
            }
            setSrcSet(
              isBrowserUrl(cloud800) ? `${ready400} 1x, ${cloud800} 2x` : undefined
            );
          }
        } catch {
          /* non-fatal; stay on local until network is ready */
        }
      }
      return; // Local-first path handled. Do not run legacy/network path.
    }

    // A) Instant first paint with server-signed URL if provided
    if (preUrl) {
      if (!cancelled && imgSrc !== preUrl) {
        if (!imgLoaded) {
          setImgLoaded(false);
          paintStartRef.current = performance.now(); // DEBUG start
        }
        setImgSrc(preUrl);
        setSrcSet(undefined);
      }
    }

    // B) Opportunistic upgrade to uncropped grid thumbs (server-signed first)
    try {
      const u400Server = asset?.grid?.u400 || null;
      const u800Server = asset?.grid?.u800 || null;

      if (isBrowserUrl(u400Server)) {
        if (preUrl && preUrl === u400Server) {
          if (!cancelled) {
            setSrcSet(
              isBrowserUrl(u800Server) ? `${u400Server} 1x, ${u800Server} 2x` : undefined
            );
          }
          return;
        }

        const ready400 = await preload(u400Server);
        if (!cancelled) {
          if (imgSrc !== ready400) {
            paintStartRef.current = performance.now(); // DEBUG start
            setImgSrc(ready400);
          }
          setSrcSet(
            isBrowserUrl(u800Server) ? `${ready400} 1x, ${u800Server} 2x` : undefined
          );
          return;
        }
      }

      // Fallback: ask the server for a signed previewUrl when the grid URLs
      // were not included on the asset (common for optimistic inserts).
      const aid = asset?.assetId || asset?.id || null;
      if (aid) {
        const want2x =
          (typeof window !== 'undefined' ? window.devicePixelRatio : 1) > 1.2;

        const token = await getToken?.();
        const [u400Settled, u800Settled] = await Promise.allSettled([
          getSigned(aid, token, null),
          want2x ? getSigned(aid, token, null) : Promise.resolve(null),
        ]);

        const url400 =
          u400Settled.status === 'fulfilled' ? u400Settled.value : null;
        const url800 =
          u800Settled && u800Settled.status === 'fulfilled'
            ? u800Settled.value
            : null;

        if (url400) {
          const ready400 = await preload(url400);
          if (!cancelled) {
            setImgSrc(ready400);
            setSrcSet(url800 ? `${ready400} 1x, ${url800} 2x` : undefined);
            return;
          }
        }
      }
    } catch {
      // Non-fatal; we’ll fall through to legacy path
    }

    // C) Fallback: server-signed preview (enterprise)
    // If we have no preUrl/grid url, ask Cloud Functions for a signed previewUrl.
if (!preUrl && imgPath) {
  try {
    const aid = asset?.assetId || asset?.id || null;
    const token = await getToken?.();
    const url = await getSigned(aid, token, null);
    const ready = await preload(url);
    if (!cancelled) {
      if (imgSrc !== ready) {
        paintStartRef.current = performance.now(); // DEBUG start
        setImgSrc(ready);
      }
      setSrcSet(undefined);
    }
    return;
  } catch {
    /* ignore and fall through */
  }
}


    // D) Final fallback (1×1 SVG)
    if (!cancelled) setImgSrc(FALLBACK);
  })();

  return () => {
    cancelled = true;
  };
// NOTE: do not depend on imgSrc/imgLoaded to avoid loops
}, [
  localSrc,
  cloud400,
  cloud800,
  preUrl,
  asset?.grid?.u400,
  asset?.grid?.u800,
  asset?.assetId,
  asset?.id,
  getToken,
  imgPath // keep if you want, but it's computed above; harmless
]);






  /* 3. Aspect-ratio helper (prefer thumb.aspect; allow override) */
  const naturalAspect = getThumbAspect(asset);
  const aspect = typeof overrideAspect === 'number' && overrideAspect > 0
    ? overrideAspect
    : naturalAspect;

  // Keep classes for CSS hooks; choose closest bucket
  const ratioClass =
    aspect >= 1.6
      ? 'ratio-16x9'
      : aspect >= 1.28
      ? 'ratio-4x3'
      : 'ratio-1x1';

  // Inline aspect ratio (prevents layout shift)
  const containerAspect = `${aspect.toFixed(4)} / 1`;

  // ENTERPRISE: if backend detects alpha, do NOT paint dominantColor behind it.
  // Falls back safely if older assets don't have the field.
  const hasAlpha =
    Boolean(asset?.thumb?.hasAlpha) === true ||
    Boolean(asset?.metadata?.hasAlpha) === true;

  /* 4. ⋮  menu state */
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);
  const openMenu = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };
  const closeMenu = () => setAnchorEl(null);


  // Delete confirm dialog state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  /* 5. Metadata string */
  const typeSlug = asset.assetType ?? null;
  const typeLabel = typeSlug
    ? typeSlug.charAt(0).toUpperCase() + typeSlug.slice(1)
    : '--';

  const metaLine =
    `${typeLabel} – ${asset.size ?? '--'} KB – ` +
    `${asset.dimensions?.width ?? '--'}×${asset.dimensions?.height ?? '--'}`;


  /* ------------------------------------------------------------
   * Download the ORIGINAL file
   * - Prefer CF (getAssetById with prefer:'original') → signed URL
   * - Fallback to client Storage path when available
   * ---------------------------------------------------------- */
  const handleDownloadOriginal = async () => {
    try {
      const idToken = await currentUser?.getIdToken?.();
      // Ask CF to sign the ORIGINAL; CF falls back if needed
      const out = await fetchAssetById(
        asset?.assetId || asset?.id,
        idToken,
        { prefer: 'original', includeMeta: true }
      );
      const href = out?.previewUrl || null;
      if (href) {
        const a = document.createElement('a');
        a.href = href;
        a.download = asset?.name || `asset_${asset?.assetId || asset?.id}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        return;
      }
    } catch (e) {
      // non-fatal – we’ll try Storage fallback below
      // console.warn('[AssetCard] CF download failed; trying fallback:', e);
    }

    // Fallback: direct Storage path to original (if present)
    try {
      const p = asset?.versions?.original?.path || null;
      if (!p) throw new Error('No original.path on asset');
      const url = await getDownloadURL(ref(storage, p));
      const a = document.createElement('a');
      a.href = url;
      a.download = asset?.name || `asset_${asset?.assetId || asset?.id}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e2) {
      console.warn('[AssetCard] original fallback failed:', e2);
    }
  };

   /* ------------------------------------------------------------
   * Delete performer (no confirm UI here)
   * - Called by the ConfirmDeleteDialog
   * ---------------------------------------------------------- */
  const performDelete = async () => {
    const assetId = asset?.assetId || asset?.id;
    if (!assetId) {
      console.warn('[AssetCard] performDelete: missing assetId');
      return;
    }
    try {
      const idToken = await currentUser?.getIdToken?.();
      if (!idToken) throw new Error('Not authenticated');

      //  (enterprise UX): remove tile immediately, BEFORE the slow backend delete.
      // This makes the UI feel instant even if storage cleanup takes seconds.
      try {
        window.dispatchEvent(
          new CustomEvent('asset:deleted', { detail: { assetId, optimistic: true } })
        );
      } catch (_) {}

      // Let boards remove the card immediately (primary signal)
      try { onDetails({ deleted: true, assetId, optimistic: true }); } catch (_) {}

      // Enterprise:
      // Delete must go through the environment-aware asset manager helper.
      // This keeps staging calls on:
      //   us-central1-utmsaas-96fbc.cloudfunctions.net/deleteAsset
      // and production calls on:
      //   us-central1-myutms-f8cba.cloudfunctions.net/deleteAsset
      //
      // The helper also owns timeout/error parsing, so this component does not
      // need to duplicate Cloud Function URL construction or response parsing.
      await deleteAssetViaCloudFunction(assetId, idToken);

      // Confirmed: backend delete succeeded.
      // We intentionally do NOT dispatch a second 'asset:deleted' event here to avoid
      // duplicate board updates / extra renders. If you later want "confirmed" flows,
      // introduce a separate event like 'asset:delete-confirmed'.
      console.log('[AssetCard] delete confirmed by server:', assetId);

      // Optional secondary callback for legacy callers (safe no-op)
      // (kept exactly, but now it fires after server confirms)
      try { onDeleted?.(assetId); } catch (_) {}


    } catch (e) {
      console.warn('[AssetCard] delete failed:', e?.message || e);

      // tell the app delete failed so board can reconcile (refetch/restore).
      try {
        window.dispatchEvent(
          new CustomEvent('asset:delete-failed', { detail: { assetId } })
        );
      } catch (_) {}

      alert(
        e?.name === 'AbortError'
          ? 'Delete timed out. Please try again.'
          : 'Delete failed. Please try again.'
      );
    }
  };




  /* 6. Render */
  return (
    <Card
      className="asset-card"
      elevation={0}
      onClick={(e) => {
        // Enterprise guard: never treat the card as "selected"
        // while menus/dialogs are active or deletion is in progress.
        if (deleting || menuOpen || confirmOpen || versionsOpen) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        onClick?.(e);
      }}
      sx={{
        ...(sxProp || {}),
        width: sxProp.width || 200,
        p: 0,
        m: 0,
        border: '1px solid',
        borderColor: selected ? '#1976d2' : '#ccc',
        '&:hover': { borderColor: '#1976d2' },
        alignSelf: 'start',
        height: 'auto',
        cursor: deleting ? 'default' : 'pointer',         
        pointerEvents: deleting ? 'none' : 'auto',         
        opacity: deleting ? 0.5 : 1,                        // keep dim
        backgroundColor: 'transparent',
        boxShadow: 'none',
        display: 'flex',
        flexDirection: 'column',
      }}
    >

      {/*  IMAGE CONTAINER */}
      <Box
        className={`asset-card-image-container ${ratioClass}`}
        sx={{
          aspectRatio: containerAspect,
          border: 0, // no inner border
          overflow: 'hidden',
          position: 'relative',
          backgroundColor: hasAlpha ? 'transparent' : (asset?.thumb?.dominantColor || 'transparent'),
          // prevent inline-baseline whitespace from <img>
          lineHeight: 0,
          fontSize: 0,
        }}
      >
        {/* ░░ Skeleton placeholder ░░ */}
        {!imgLoaded && (
          <Skeleton
            variant="rectangular"
            animation="wave"
            sx={{ position: 'absolute', inset: 0 }}
          />
        )}

        {/* image */}
        {(() => {
          // the effect is the single source of truth for the current image URL.
          // It sets imgSrc to preUrl immediately, then upgrades to grid when ready.
          // So we render imgSrc directly (no need for `const primary = preUrl || imgSrc`).
          return (
            <CardMedia
              className={`asset-card-image ${forceContain ? 'contain' : ''}`}
              component="img"
              src={imgSrc}
              srcSet={srcSet}
              sizes={renderWidth ? `${renderWidth}px` : undefined}
              alt={asset.name}
              draggable={false}
              loading={loadingPriority === 'high' ? 'eager' : 'lazy'}   // ← NEW
              {...(loadingPriority === 'high' ? { fetchpriority: 'high' } : {})} // ← NEW
              decoding="async"
              onLoad={() => {
                setImgLoaded(true);
                if (paintStartRef.current) {
                  console.log(
                    '[Card:onLoad ms]',
                    Math.round(performance.now() - paintStartRef.current),
                    {
                      id: asset.id || asset.assetId,
                      src: imgSrc,
                    }
                  );
                  paintStartRef.current = 0; // reset
                }
              }}
              onError={async () => {
                console.warn('[AssetCard] image error:', imgSrc);
                if (imgPath && imgSrc !== FALLBACK) {
                  try {
                    const aid = asset?.assetId || asset?.id || null;
                    const token = await getToken?.();
                    const url = await getSigned(aid, token, null);
                    const ready = await preload(url);
                    setImgSrc(ready);
                    setSrcSet(undefined);
                    return;
                  } catch { /* ignore */ }
                }
                if (imgSrc !== FALLBACK) {
                  setImgSrc(FALLBACK);
                  setSrcSet(undefined);
                }
              }}
              sx={{
                display: imgLoaded ? 'block' : 'none',   // hide until first success
                width: '100%',
                height: '100%',
                // IMPORTANT for DAM: no zoom/crop when forceContain is true.
                objectFit: forceContain ? 'contain' : 'cover',
                objectPosition: 'center',
              }}
            />

          );
        })()}


        {/* Versions (top-left) – only when multiple versions exist; fade-in on hover */}
        {hasVersions && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
   console.log('[VersionsClick] icon clicked for', (asset?.id || asset?.assetId));

              if (!deleting) setVersionsOpen(true);
   console.log('[VersionsClick] setVersionsOpen(true) called');
            
            }}
            disabled={deleting}
            aria-label="View versions"
            className="asset-card-versions"
            sx={{
              position: 'absolute',
              top: 6,
              left: 6,
              p: '4px',
              borderRadius: '50%',
              backgroundColor: 'rgba(0,0,0,0.50)',
              color: '#fff',
              zIndex: 20,
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.65)' },
            }}
          >
            <LayersIcon sx={{ fontSize: '1rem' }} />
          </IconButton>
        )}

        {/* ⋮ More – visibility now handled by CSS */}
        <IconButton
          size="small"
          onClick={openMenu}
          disabled={deleting}                // ← prevent double actions
          aria-busy={deleting ? 'true' : 'false'}
          className="asset-card-more-options"
          sx={{
            position: 'absolute',
            top: 6,
            right: 6,
            p: '4px',
            borderRadius: '50%',
            backgroundColor: 'rgba(0,0,0,0.50)',
            color: '#fff',
            zIndex: 20,
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.65)' },
          }}
        >
          <MoreVertIcon sx={{ fontSize: '1rem' }} />
        </IconButton>


        {/* ░░ bottom-left overlay (name + meta) – class enables hover fade-in ░░ */}
        <Box
          className="asset-card-overlay"
          sx={{
            position: 'absolute',
            bottom: 6,
            left: 6,
            backgroundColor: 'rgba(0,0,0,0.50)',
            color: '#fff',
            px: 0.5,
            py: 0.25,
            borderRadius: '4px',
            maxWidth: '90%',
          }}
        >
          {/* Name */}
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              fontWeight: 600,
              fontSize: '0.80rem',
              lineHeight: 1.15,
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
            }}
          >
            {asset.name}
          </Typography>

          {/* Metadata */}
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              fontSize: '0.70rem',
              lineHeight: 1.1,
              mt: '2px',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
            }}
          >
            {metaLine}
          </Typography>
        </Box>
                {/* Dim overlay while deleting */}
        {deleting && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(0,0,0,0.12)',
              pointerEvents: 'none',
            }}
          />
        )}

      </Box>

      {/* ⋮  pop-up menu (match VersionsDrawer look) */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top',    horizontal: 'right' }}
        PaperProps={{
          sx: {
            width: 'auto',
            minWidth: '12ch',
            maxWidth: '16ch',
            p: 0,
            borderRadius: 1,
            boxShadow: 3,
          },
        }}
        MenuListProps={{
          dense: true,
          disablePadding: false,
        }}
      >
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            try { onDetails(asset); } catch (_) {}
            closeMenu();
          }}
          sx={{
            /* force same type scale as VersionsDrawer rows */
            typography: 'body2',
            px: 1.5,
            py: 0.5,
          }}
        >
          Details
        </MenuItem>

        <MenuItem
          onClick={async (e) => {
            e.stopPropagation();
            await handleDownloadOriginal();
            closeMenu();
          }}
          sx={{
            typography: 'body2',   /*  keep type scale consistent */
            px: 1.5,
            py: 0.5,
          }}
        >
          Download (original)
        </MenuItem>

        <MenuItem
          disabled={deleting}
          onClick={(e) => {
            e.stopPropagation();
            closeMenu();
            setConfirmOpen(true);
          }}
          sx={{
            px: 1.5,
            py: 0.5,
            color: 'error.main',
            '&:hover': { bgcolor: 'error.light', color: 'error.contrastText' },
          }}
        >
          Delete
        </MenuItem>

      </Menu>
      {/* Delete confirm dialog */}
      <ConfirmDeleteDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={async () => {
          try {
            setConfirmOpen(false); // close immediately (UI feels instant)
            setDeleting(true);
            await performDelete();
          } finally {
            setDeleting(false);
          }
        }}
        asset={asset}
        busy={deleting}
      />

      
      {/* Versions drawer */}
      <VersionsDrawer
        open={versionsOpen}
        onClose={() => setVersionsOpen(false)}
        organizationId={asset?.organizationId || ''}
        currentAsset={{
          // normalize to what the drawer expects
          ...asset,
          id: asset?.id || asset?.assetId,
        }}
        onSelectVersion={(row) => {
          // close the drawer; parent can react via onDetails if desired
          setVersionsOpen(false);
          try {
            // bubble an informational event; parent can navigate/open details
            onDetails?.({ selectedVersion: row });
          } catch (_) {}
        }}
        onAddNewVersion={() => {
          // Optional: bubble a request; leave as no-op if not needed
          try { onDetails?.({ requestNewVersion: true }); } catch (_) {}
        }}
      />

    </Card>
  );
}

/* ────────────────────────────────────────────────────────────────
   PropTypes
───────────────────────────────────────────────────────────────── */
AssetCard.propTypes = {
  asset: PropTypes.shape({
    // (optional) ids in either field; card normalizes internally
    id: PropTypes.string,
    assetId: PropTypes.string,

    // existing versions shape (kept) + optional extras used by drawers
    versions: PropTypes.shape({
      thumbnail: PropTypes.shape({
        path: PropTypes.string,
        width: PropTypes.number,
        height: PropTypes.number,
        formIds: PropTypes.arrayOf(PropTypes.string),
      }),
      original: PropTypes.shape({
        path: PropTypes.string,
        width: PropTypes.number,
        height: PropTypes.number,
        formIds: PropTypes.arrayOf(PropTypes.string),
      }),
      // optional legacy renditions (kept if present)
      medium: PropTypes.shape({
        path: PropTypes.string,
        width: PropTypes.number,
        height: PropTypes.number,
      }),
      large: PropTypes.shape({
        path: PropTypes.string,
        width: PropTypes.number,
        height: PropTypes.number,
      }),
      icon2x: PropTypes.shape({
        path: PropTypes.string,
        width: PropTypes.number,
        height: PropTypes.number,
      }),
    }),

    // (rare) literal URL/placeholder kept from older data
    thumbnail: PropTypes.string,

    // required display name
    name: PropTypes.string.isRequired,

    // existing createdAt union (kept exactly)
    createdAt: PropTypes.oneOfType([
      PropTypes.instanceOf(Date),
      PropTypes.object, // Firestore Timestamp
      PropTypes.string,
    ]),

    // existing core fields (kept)
    assetType: PropTypes.string,
    size: PropTypes.number,
    dimensions: PropTypes.shape({
      width: PropTypes.number,
      height: PropTypes.number,
    }),

    // server-provided pre-signed URL from backend (kept)
    thumbUrl: PropTypes.string,

    // grid thumbs signed by backend.fetchAssets (kept)
    grid: PropTypes.shape({
      u400: PropTypes.string,
      u800: PropTypes.string,
    }),

    // NEW: optimistic/upgrade fields used by local→cloud swap
    // (localSrc is a blob: URL set by the context; do NOT revoke in the card)
    localSrc: PropTypes.string,
    // (cloudSrc is an early 400w URL the context may set; card also
    //  supports grid.u400/u800 coming from the server response)
    cloudSrc: PropTypes.string,

    // NEW: lifecycle status from the worker flow
    // ('processing' → 'published' or 'error')
    status: PropTypes.string,

    // optional promoted/thumb metadata (kept if backend adds them)
    thumb: PropTypes.shape({
      aspect: PropTypes.number,
      dominantColor: PropTypes.string,
      urls: PropTypes.object, // {"400": pathOrUrl, "800": pathOrUrl}
      micro: PropTypes.object, // {"32": pathOrUrl, "64": pathOrUrl}
    }),

    // optional versioning helpers (drawer/icon logic)
    versionNumber: PropTypes.number,
    previousAssetId: PropTypes.string,
  }).isRequired,

  onClick: PropTypes.func,
  onDetails: PropTypes.func,
  onDeleted: PropTypes.func,
  selected: PropTypes.bool,

  // layout/behavior props (kept)
  overrideAspect: PropTypes.number,
  forceContain: PropTypes.bool,
  loadingPriority: PropTypes.oneOf(['auto', 'high']),
  renderWidth: PropTypes.number,
  sx: PropTypes.object,

};


// Memo comparator: ignore function identity; compare essential fields only.
// This prevents re-renders caused by inline onClick/sx objects in the map.
const areEqual = (prev, next) => {
  const a = prev.asset || {};
  const b = next.asset || {};

  // 1) Identity / stable fields
  const idA = a.id || a.assetId;
  const idB = b.id || b.assetId;
  if (idA !== idB) return false;

  // 2) URLs that affect the image shown
  if ((a.thumbUrl || null) !== (b.thumbUrl || null)) return false;

  // 2.1) Grid URLs (so upgrades trigger a render)
  const ag4 = a.grid?.u400 || null;
  const ag8 = a.grid?.u800 || null;
  const bg4 = b.grid?.u400 || null;
  const bg8 = b.grid?.u800 || null;
  if (ag4 !== bg4 || ag8 !== bg8) return false;

  // 2.2) Local/Cloud swap flags from context
  if ((a.localSrc || null) !== (b.localSrc || null)) return false;
  if ((a.cloudSrc || null) !== (b.cloudSrc || null)) return false;

  // 2.3) Status (processing → published → error)
  if ((a.status || null) !== (b.status || null)) return false;

  // 2.5) Aspect used by layout reservation
  const ta = a.thumb?.aspect ?? null;
  const tb = b.thumb?.aspect ?? null;
  if (ta !== tb) return false;

  // 3) Dimensions/aspect that affect layout or aspect-ratio
  const aw = a.dimensions?.width || null;
  const ah = a.dimensions?.height || null;
  const bw = b.dimensions?.width || null;
  const bh = b.dimensions?.height || null;
  if (aw !== bw || ah !== bh) return false;

  // 3.5) Type/size that affect overlay text
  if ((a.assetType || null) !== (b.assetType || null)) return false;
  if ((a.size || null) !== (b.size || null)) return false;

  // 4) Selected state toggles border color
  if ((prev.selected || false) !== (next.selected || false)) return false;

  // 4.5) New layout/image props that affect rendering
  if ((prev.overrideAspect ?? null) !== (next.overrideAspect ?? null)) {
    return false;
  }
  if ((prev.forceContain || false) !== (next.forceContain || false)) {
    return false;
  }

  // 5) Only compare the layout-critical sx fields
  const sxA = prev.sx || {};
  const sxB = next.sx || {};
  const colA = sxA.gridColumn || null;
  const colB = sxB.gridColumn || null;
  const rowA = sxA.gridRowEnd || null;
  const rowB = sxB.gridRowEnd || null;
  if (colA !== colB || rowA !== rowB) return false;

  return true;
};


export default React.memo(AssetCard, areEqual);
